import { useExportDuoInvoiceBatchMutation } from "@/api/mutations/duo/useExportDuoInvoiceBatchMutation"
import { useDuoInvoiceBatchExportsInfiniteQuery } from "@/api/queries/duo/useDuoInvoiceBatchExportsInfiniteQuery"
import type {
  DuoExport,
  DuoInvoiceBatchExportValidationError,
} from "@/api/types/duo"
import { DuoSidebarActionButton } from "@/components/duo/DuoSidebarActionButton"
import { DuoWorkspaceLayout } from "@/components/duo/DuoWorkspaceLayout"
import { InvoiceBatchDetail } from "@/components/duo/InvoiceBatchDetail"
import { checkPermissions } from "@/utils/checkPermissions"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import {
  ApiValidationError,
  Button,
  PageHeader,
  Skeleton,
  cn,
  formatDateToDutch,
} from "toptaal-shared"
import { z } from "zod"

const searchSchema = z.object({
  exportId: z.number().optional(),
})

export const Route = createFileRoute("/duo/facturen/")({
  component: RouteComponent,
  validateSearch: searchSchema,
  beforeLoad: () => checkPermissions("read", "duo"),
})

function RouteComponent() {
  const { exportId } = useSearch({ from: "/duo/facturen/" })
  const navigate = useNavigate({ from: "/duo/facturen/" })
  const {
    data: historyData,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useDuoInvoiceBatchExportsInfiniteQuery()
  const exports = historyData?.exports ?? []
  const selectedExport = getSelectedExport(exports, exportId)
  const exportMutation = useExportDuoInvoiceBatchMutation()
  const validationErrors = getInvoiceBatchValidationErrors(exportMutation.error)

  const selectExport = (selectedId: number) => {
    navigate({ search: { exportId: selectedId }, replace: true })
  }

  const createExport = () => {
    exportMutation.mutate(undefined, {
      onSuccess: () => navigate({ search: {}, replace: true }),
    })
  }

  return (
    <DuoWorkspaceLayout
      sidebar={
        <>
          <PageHeader>DUO facturen</PageHeader>

          <DuoSidebarActionButton
            isLoading={exportMutation.isPending}
            loadingLabel="Export maken..."
            onClick={createExport}
          >
            Nieuwe export
          </DuoSidebarActionButton>

          {validationErrors.length > 0 && (
            <InvoiceBatchValidationErrors errors={validationErrors} />
          )}

          {exportMutation.isError && validationErrors.length === 0 && (
            <p className="mt-4 text-sm text-red" role="alert">
              De DUO-facturenexport kon niet worden aangemaakt.
            </p>
          )}

          <div className="mt-3 border-t border-primary-accent-blue pt-3">
            {isLoading && <ExportHistorySkeleton />}

            {isError && (
              <p className="text-sm text-red" role="alert">
                De exportgeschiedenis kon niet worden geladen.
              </p>
            )}

            {!isLoading && !isError && exports.length === 0 && (
              <p className="py-3 text-sm text-primary-dark-blue/70">
                Er zijn nog geen facturen geëxporteerd.
              </p>
            )}

            <div className="flex flex-col gap-2">
              {exports.map((duoExport) => {
                const isSelected = selectedExport?.id === duoExport.id

                return (
                  <button
                    key={duoExport.id}
                    type="button"
                    aria-current={isSelected ? "page" : undefined}
                    className={cn(
                      "flex h-11 w-full items-center justify-between rounded px-4 text-left transition-colors",
                      isSelected
                        ? "bg-primary-green text-white"
                        : "bg-light-grey hover:bg-primary-light-blue",
                    )}
                    onClick={() => selectExport(duoExport.id)}
                  >
                    <span>{formatDateToDutch(duoExport.insertedAt)}</span>
                    <ChevronRightIcon className="size-5" />
                  </button>
                )
              })}

              {hasNextPage && (
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-1 w-full"
                  isLoading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  Meer laden
                </Button>
              )}
            </div>
          </div>
        </>
      }
    >
      {selectedExport ? (
        <InvoiceBatchDetail duoExport={selectedExport} />
      ) : (
        !isLoading && (
          <div className="flex h-full min-h-56 items-center justify-center text-center text-primary-dark-blue/70">
            Maak een nieuwe export om facturen naar DUO te sturen.
          </div>
        )
      )}
    </DuoWorkspaceLayout>
  )
}

function getSelectedExport(exports: DuoExport[], exportId?: number) {
  return exports.find((duoExport) => duoExport.id === exportId) ?? exports[0]
}

function getInvoiceBatchValidationErrors(error: unknown) {
  if (!(error instanceof ApiValidationError)) return []

  const response = error.response.data as unknown as {
    errors?: DuoInvoiceBatchExportValidationError[]
  }

  return response?.errors ?? []
}

function InvoiceBatchValidationErrors({
  errors,
}: {
  errors: DuoInvoiceBatchExportValidationError[]
}) {
  return (
    <div
      className="mt-4 rounded border border-red/30 bg-red/5 p-3"
      role="alert"
    >
      <p className="text-sm font-bold text-red">
        De export bevat ongeldige factuurgegevens:
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-primary-dark-blue">
        {errors.map((error, index) => (
          <li key={`${error.invoiceId ?? "error"}-${index}`}>
            {error.invoiceNumber && `${error.invoiceNumber}: `}
            {error.errors?.join(", ") ?? error.message ?? "Onbekende fout"}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ExportHistorySkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-label="Exportgeschiedenis laden">
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-11 w-full" />
      ))}
    </div>
  )
}
